import React from "react";
import ResultCard from "../results/ResultCard";
import useCheckProjectPermissions from "../../hooks/useCheckProjectPermissions";
import { Link } from "react-router-dom";
import { HiOutlineDocumentReport, HiOutlinePlus } from "react-icons/hi";

const ResultsTab = ({ projectData }) => {
  const { project, results } = projectData ?? {};
  const { hasAccessToResult } = useCheckProjectPermissions({
    projectData: project,
  });
 

  return (
    <div className="w-full flex flex-col pt-6">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
          <HiOutlineDocumentReport className="w-5 h-5 text-white" />
        </div>
        <h5 className="text-xl font-bold text-secondary">Results</h5>
        <span className="ml-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
          {results?.length || 0} published
        </span>
      </div>
      
      <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
        Discover the progress and findings of this project, publicly shared in alignment 
        with the vision of DeSci. Premium findings are available for project supporters.
      </p>
      
      {project?.state === "Draft" ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-20 h-20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <p className="text-lg font-medium">Project in Draft Mode</p>
          <p className="text-sm">Results will be available once the project is published</p>
        </div>
      ) : !results?.length && !hasAccessToResult ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-gray-600 mb-2">No Results Published Yet</p>
          <p className="text-sm text-gray-500 max-w-md text-center">Research findings and progress updates will appear here once the project team shares them with the community</p>
        </div>
      ) : (
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
          {results?.length
            ? results?.map((item) => (
                <ResultCard
                  resultData={item}
                  projectData={project}
                  key={item?._id}
                />
              ))
            : null}
          {hasAccessToResult && (
            <Link
              to={`/create-result?project-id=${project?._id}&new=true`}
              className="group relative w-full min-h-[260px] flex flex-col items-center justify-center rounded-2xl overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-50 to-primary/10 group-hover:from-primary/10 group-hover:via-purple-100 group-hover:to-primary/20 transition-all duration-500" />
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-300/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
              
              {/* Content */}
              <div className="relative flex flex-col items-center gap-4 z-10 px-8 text-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-[#7e7cff] rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                  <div className="relative w-16 h-16 rounded-full bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center text-white shadow-2xl group-hover:scale-110 group-hover:rotate-90 transition-all duration-300">
                    <HiOutlinePlus className="w-8 h-8" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-700 group-hover:text-primary transition-colors">
                    {results?.length ? "Submit Another Result" : "Submit Your First Result"}
                  </h3>
                  <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
                    {results?.length 
                      ? "Continue sharing your research progress and discoveries"
                      : "Share your findings with funders and the scientific community"}
                  </p>
                </div>
                
                <div className="mt-2 px-6 py-2.5 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-full text-sm font-semibold shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300">
                  Get Started →
                </div>
              </div>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultsTab;
